import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import postal from 'postal';
import toastr from 'toastr';
import Modal from 'react-bootstrap/Modal';
import Loading from '../../../shared/loading';
import parseFailXhr from '../../../shared/parse-fail-xhr';
import Validator from '../../../shared/validator';
import ErrorBoundary from '../../../shared/error-boundary';
import { EMAIL_REGEX } from '../../../shared/consts';
import signInUser from '../../../services/auth/sign-in-user';
import oauthProviderDisplayName from '../../shared/sign-up/oauth-provider-label';

export default class SignInModal extends React.Component {
  static propTypes = {
    redirectTo: PropTypes.string,
  };

  static defaultProps = {
    redirectTo: null,
  };

  constructor(props, context) {
    super(props, context);

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleEmailChange = this.handleEmailChange.bind(this);
    this.handlePasswordChange = this.handlePasswordChange.bind(this);
    this.present = this.present.bind(this);
    this.dismiss = this.dismiss.bind(this);
    this.createNewAccount = this.createNewAccount.bind(this);
    this.onModalClick = this.onModalClick.bind(this);

    this.state = _.merge(
      this.state,
      {
        show: false,
        email: '',
        password: '',
        invalidCredentials: false,
        emailOnlyError: false,
        oauthProvider: null,
        validator: new Validator({
          emailRequired: state => !!state.email,
          emailRegex: state => !state.email || EMAIL_REGEX.test(state.email),
          passwordRequired: state => !!state.password,
          credentialValidity: state => !state.invalidCredentials,
        }),
        redirectTo: props.redirectTo,
      },
    );
    this.subscriptions = [];
  }

  componentDidMount() {
    this.subscriptions.push(postal.subscribe({
      channel: 'commands.sign-in',
      topic: 'present',
      callback: data => {
        if (_.get(data, 'redirectTo')) {
          this.setState({ redirectTo: data.redirectTo });
        }

        this.present();
      },
    }));
    this.subscriptions.push(postal.subscribe({
      channel: 'commands.sign-in',
      topic: 'dismiss',
      callback: () => this.dismiss(),
    }));
  }

  componentWillUnmount() {
    this.subscriptions.forEach(s => postal.unsubscribe(s));
    this.subscriptions = [];
  }

  handleSubmit(event) {
    event.preventDefault();

    this.setState(
      state => {
        state.invalidCredentials = false;
        state.validator.validateForm(state);

        return state;
      },
      () => {
        if (!this.state.validator.formValid) return;

        this.loading.start();

        signInUser(this.state.email, this.state.password)
          .then(() => {
            if (this.state.redirectTo) {
              window.location = this.state.redirectTo;
            } else if (_.startsWith(window.location.pathname, '/campaigns/')) {
              window.location = '/';
            } else {
              window.location.reload();
            }
          }).catch(err => {
            this.loading.stop();

            const errors = err.jqXHR.status === 401
              ? parseFailXhr({
                responseJSON: {
                  invalidCredentials: [
                    'invalid',
                  ],
                },
              })
              : parseFailXhr(err.jqXHR);

            if (errors.invalidCredentials) {
              this.setState(state => {
                state.invalidCredentials = true;
                state.validator.validateCredentialValidity(state);

                return state;
              });
              errors.invalidCredentials.invalid.handled = true;
            }

            // Check for "Email Only" error
            if (errors.error) {
              const errorKeys = Object.keys(errors.error);
              const emailOnlyError = errorKeys.find(key => {
                const errorMessage = errors.error[key].message;
                return errorMessage && (errorMessage.includes('complete registration') || errorMessage.includes('Email Only'));
              });
              if (emailOnlyError) {
                const errorData = _.get(err, 'jqXHR.responseJSON', {});
                const provider = errorData.provider || null;
                this.setState({ emailOnlyError: true, oauthProvider: provider });
                errors.error[emailOnlyError].handled = true;
              }
            }

            if (!errors.anyUnhandledErrors()) return;

            toastr.error(errors.getErrorSummary(), 'Error');
          });
      },
    );
  }

  handleEmailChange(event) {
    const email = event.target.value;

    this.setState(state => {
      state.email = email;
      state.validator.validateEmailRequired(state);
      state.validator.validateEmailRegex(state);
      state.invalidCredentials = false;
      state.emailOnlyError = false;
      state.oauthProvider = null;
      state.validator.validateCredentialValidity(state);

      return state;
    });
  }

  handlePasswordChange(event) {
    const password = event.target.value;

    this.setState(state => {
      state.password = password;
      state.validator.validatePasswordRequired(state);
      state.invalidCredentials = false;
      state.emailOnlyError = false;
      state.oauthProvider = null;
      state.validator.validateCredentialValidity(state);

      return state;
    });
  }

  onModalClick(e) {
    if (e.target.className.indexOf('sign-in-modal') >= 0) {
      this.dismiss();
    }
  }

  present() {
    this.setState({ show: true });
  }

  dismiss() {
    this.setState({ show: false });
  }

  createNewAccount() {
    this.dismiss();
    postal.publish({
      channel: 'commands.sign-up',
      topic: 'present',
    });
  }

  render() {
    const { validator } = this.state;
    return (
      <ErrorBoundary>
        <Modal
          centered="true"
          dialogAs="div"
          dialogClassName="sign-in-modal"
          show={this.state.show}
          onHide={this.dismiss}
          onClick={this.onModalClick}
          onEnter={() => this.emailInput.focus()}
        >
          <article>
            <header>
              <button type="button" className="close-modal" onClick={this.dismiss}>
                <span className="pe-7s-close pe-icon-x3" />
                <span className="sr-only">Close</span>
              </button>
              <h4>Sign in to Livewire</h4>
            </header>
            <section>
              <form onSubmit={this.handleSubmit}>
                <Loading ref={el => (this.loading = el)} variant="small">
                  <React.Fragment>
                    <div className="livewire-modal-form-container">
                      <p
                        className={classNames('form-group', {
                          'has-error': validator.emailRequiredValid && validator.emailRegexValid,
                        })}
                      >
                        <input ref={el => (this.emailInput = el)} type="email" placeholder="Email" onChange={this.handleEmailChange} />
                        {validator.emailRequiredValid || <span className="help-block">Email address is required</span>}
                        {validator.emailRegexValid || <span className="help-block">Email address is invalid</span>}
                      </p>
                      <p className={classNames('form-group', { 'has-error': validator.passwordRequiredValid })}>
                        <input type="password" placeholder="Password" onChange={this.handlePasswordChange} />
                        {validator.passwordRequiredValid || <span className="help-block">Password is required</span>}
                      </p>
                    </div>
                    {this.state.emailOnlyError && (
                      <p>
                        <span className="help-block">
                          {this.state.oauthProvider ? (
                            <>
                              {'You have an "Email Only" account using'}
                              {' '}
                              {oauthProviderDisplayName(this.state.oauthProvider)}
                              {' '}
                              {'oAuth. Sign in using'}
                              {' '}
                              {oauthProviderDisplayName(this.state.oauthProvider)}
                              {' '}
                              {'oAuth to access your account.'}
                            </>
                          ) : (
                            <>
                              {'You have an "Email Only" account and need to'}
                              {' '}
                              <a href="/sign_up">complete registration</a>
                              {' '}
                              {'before you can Sign In'}
                            </>
                          )}
                        </span>
                      </p>
                    )}
                    {validator.credentialValidityValid || (
                      <p>
                        <span className="help-block">
                          The email or password you supplied is invalid. Did you
                          mean to
                          {' '}
                          <a href="#" onClick={this.createNewAccount}>create a new account</a>
                          {' instead?'}
                        </span>
                      </p>
                    )}
                    <p className="form-group submit-group">
                      <button type="submit" className="btn btn-primary">Sign in</button>
                    </p>
                    <p className="form-group">
                      <a href="/users/password/new">Forgot your password?</a>
                      <br />
                      <a href="/users/unlock/new">Resend unlock instructions</a>
                    </p>
                  </React.Fragment>
                </Loading>
              </form>
            </section>
          </article>
        </Modal>
      </ErrorBoundary>
    );
  }
}
